import React from "react";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { FormattedMessage } from "react-intl";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// material-ui
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Stack,
  TextField,
  FormControl,
  FormControlLabel,
  Select,
  Typography,
  CircularProgress,
  Backdrop,
  Switch,
  Chip,
  useTheme,
  FormHelperText,
} from "@mui/material";
import { DatePicker } from "antd";
import ImageUploader from "components/ImageUploader";

// third party
import * as Yup from "yup";
import { Formik, Field } from "formik";
import dayjs from "dayjs";

// project import
import { openSnackbar } from "api/snackbar";
import MainCard from "components/MainCard";
// types
import { SnackbarProps } from "../../../../../types/snackbar";

import { USERAXIOS } from "../../../../../api/userAxios";
import { City, Country } from "../../../../../types/site-params/Country";
import { Category } from "../../../../../types/site-params/Category";
import {
  clientOptions,
  cultureOptions,
  financialAbilityOptions,
  mockCompanyProfile,
  ourContactOptions,
  serviceOptions,
} from "./mockData";
import { Title } from "../../../../../types/title";
import { CompanyActivity } from "../../../../../types/companyActivity";
import { User } from "../../../../../model/user";
import { useCountriesForAutocomplete } from "../../../../../hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "../../../../../hooks/query-hooks/useCities";
import { useDebounce } from "../../../../../hooks";

// ==============================|| TAB - PERSONAL ||============================== //

const AddEditCompanyProfile = () => {
  const navigate = useNavigate();
  const maxDate = new Date();
  const theme = useTheme();
  const { companyId } = useParams();

  maxDate.setFullYear(maxDate.getFullYear() - 18);

  const [selectednationality, setNationality] = useState(19);

  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedCity, setSelectedCity] = useState<number | null>(null);
  const [profileData, setProfileData] = useState(mockCompanyProfile);
  const [titles, setTitles] = useState<Title[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedMobileCode, setSelectedMobileCode] = useState("+963");
  const [selectedPhoneCode, setSelectedPhoneCode] = useState("+963");
  const [isLoading, setIsLoading] = useState(false);
  const [activities, setActivities] = useState<CompanyActivity[]>([]);
  const [selectedActivities, setSelectedActivities] = useState<
    CompanyActivity[]
  >([]);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    name_en: Yup.string().required("Name is required"),
    name_trans: Yup.object().shape({
      ar: Yup.string().required("Arabic Name is required"),
    }),
    description_en: Yup.string().required("Description is required"),
    description_trans: Yup.object().shape({
      ar: Yup.string().required("Arabic Description is required"),
    }),
    url: Yup.string().required("Url full name is required"),
    post_office: Yup.date().required("Post Office is required"),
    address_en: Yup.string().required("Address English is required"),
    address_trans: Yup.object().shape({
      ar: Yup.string().required("Address Arabic is required"),
    }),
    email: Yup.string().required("Email is required"),
    phone_number: Yup.string(),
    employee_phone_number: Yup.string(),
  });

  const fetchCategories = async () => {
    try {
      const response = await USERAXIOS.get("/category?page=1");
      if (response.data.data) {
        setCategories(response.data.data);
      }
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching categories",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await USERAXIOS.get("/countries?parent_id=");
      if (response.data.data) {
        setCountries(response.data.data);
      }
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching countries",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    }
  };

  const fetchCitiesByCountry = async (countryId: number) => {
    try {
      const response = await USERAXIOS.get(`/countries?parent_id=${countryId}`);
      if (response.data.data) {
        setCities(response.data.data);
      }
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching cities",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
      setCities([]);
      setSelectedCity(null);
      // setSelectedCityName("");
    }
  };

  const fetchTitleData = async () => {
    try {
      const response = await USERAXIOS.get("/titles?page=1");
      if (response.data.data) {
        setTitles(response.data.data);
      }
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching titles",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await USERAXIOS.get("/company_activities?page=1");
      if (response.data.data) {
        setActivities(response.data.data);
      }
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching company activities",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await USERAXIOS.get(
        "/users/select_list?account_type=GENERAL"
      );
      if (response.data && Array.isArray(response.data)) {
        setUsers(response.data);
      } else if (response.data && response.data.data) {
        setUsers(response.data.data);
      }
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching users",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: { color: "error" },
      } as SnackbarProps);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    // Fetch all necessary data when the component mounts
    fetchCountries();
    fetchCategories();
    fetchTitleData();
    fetchActivities();
    fetchUsers();
  }, []);

  useEffect(() => {
    const setupProfile = async () => {
      if (countries.length === 0) return;

      if (companyId) {
        await fetchCompanyProfile(companyId);
      } else {
        const syria = countries.find((c) => c.id === 19);
        if (syria) {
          setProfileData((prev) => ({
            ...prev,
            country_id: 19,
            nationality_id: syria.id,
          }));
          setSelectedCountry(19);
          await fetchCitiesByCountry(19);
        }
      }
    };

    setupProfile();
  }, [companyId, countries]);

  const fetchCompanyProfile = async (id: string) => {
    try {
      setIsLoading(true);
      const response = await USERAXIOS.get(`/company_profiles/${id}`);
      const data = response.data.data;

      setSelectedMobileCode(data.phone_number?.split("-")[0] || "+963");
      setSelectedPhoneCode(data.employee?.phone?.split("-")[0] || "+963");

      if (data.country_id) {
        setSelectedCountry(data.country_id);

        const selectedCountryData = countries?.find(
          (c) => c.id === data.country_id
        );
        if (selectedCountryData) {
          if (!data.nationality_id) {
            setNationality(selectedCountryData.id);
          }
        }

        await fetchCitiesByCountry(data.country_id);

        if (data.city_id) {
          setSelectedCity(data.city_id);
        }
      }

      if (data.nationality_id) {
        setNationality(data.id);
      }

      if (data.company_activities) {
        setSelectedActivities(data.company_activities);
      }

      if (data.categories) {
        setCategories(data.categories);
      }

      const employeeData = data.employee || {};
      const updatedProfileData = {
        ...data,
        phone_number: data.phone_number?.split("-")[1] || "",
        employee_phone_number:
          employeeData.phone?.split("-")[1] || employeeData.phone || "",
        mobile: employeeData.mobile?.split("-")[1] || employeeData.mobile || "",
        country: data.country_id || null,
        city: data.city_id || null,
        full_name: employeeData.full_name || "",
        employee_email: employeeData.employee_email || "",
        title_id: employeeData.title_id || "",
        position: employeeData.position || "",
      };
      setProfileData(updatedProfileData);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Failed to fetch company profile",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: { color: "error" },
      });
    } finally {
      setIsLoading(false);
    }
  };

  //TODO  flag dropdown & phone number input

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <section>
        <Stack direction={"row"}>
          <Stack direction={"column"}>
            <Typography
              variant="h4"
              sx={{
                textAlign: "center",
                color: "primary.main",
                marginBottom: "3rem",
              }}
            >
              <FormattedMessage
                id={companyId ? "Edit Company Profile" : "Add Company Profile"}
              />
            </Typography>
            <Formik
              key={JSON.stringify(profileData)}
              initialValues={{
                user_id: profileData?.user_id || null,
                name_en: profileData?.name_en || "",
                name_trans: { ar: profileData?.name_trans?.ar || "" },
                description_en: profileData?.description_en || "",
                description_trans: {
                  ar: profileData?.description_trans?.ar || "",
                },
                url: profileData?.url || "",
                email: profileData?.email || "",
                post_office: profileData?.post_office || "",
                address_en: profileData?.address_en || "",
                address_trans: { ar: profileData?.address_trans?.ar || "" },
                phone_number: profileData?.phone_number || "",
                employee_phone_number: profileData?.employee_phone_number || "",
                nationality_id:
                  countries.find(
                    (c) => c.id === (profileData?.nationality_id || 19)
                  ) || null,
                city: profileData?.city_id || "",
                country: profileData?.country_id || "",
                full_name: profileData?.full_name || "",
                employee_email: profileData?.employee_email || "",
                title_id: profileData?.title_id || "",
                position: profileData?.position || "",
                mobile: profileData?.mobile || "",
                commercial_register: profileData?.commercial_register || "",
                employees_num: profileData?.employees_num || 0,
                expected_employees_num:
                  profileData?.expected_employees_num || 0,
                company_logo: profileData?.logo || "",
                company_type: profileData?.company_type || "",
                our_contact: profileData?.our_contact || "",
                client: profileData?.client || "",
                culture: profileData?.culture || "",
                financial_ability: profileData?.financial_ability || "",
                service_required: profileData?.service_required || "",
                registration_date: profileData?.registration_date || "",
                apply_url: profileData?.apply_url || "",
                contacted: profileData?.contacted || false,
                satisfied: profileData?.satisfied || false,
                hr_dept: profileData?.hr_dept || false,
                trusted: profileData?.trusted || false,
                apply_on_other_site: profileData?.apply_on_other_site || false,
                pdf_mode: profileData?.pdf_mode || false,
                optional_end_date: profileData?.optional_end_date || false,
                submit: null,
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  const formData = new FormData();

                  console.log("values", values);
                  // Basic Info
                  formData.append("user_id", String(values.user_id || "12"));
                  formData.append("name_en", values.name_en || "");
                  formData.append(
                    "name_trans[ar]",
                    values.name_trans?.ar || ""
                  );
                  formData.append(
                    "description_en",
                    values.description_en || ""
                  );
                  formData.append(
                    "description_trans[ar]",
                    values.description_trans?.ar || ""
                  );
                  formData.append("url", values.url || "");
                  formData.append("post_office", values.post_office || "");
                  formData.append("address_en", values.address_en || "");
                  formData.append(
                    "address_trans[ar]",
                    values.address_trans?.ar || ""
                  );
                  formData.append(
                    "nationality_id",
                    String(values.nationality_id?.id || 19)
                  );
                  // formData.append(
                  //   "category_ids",
                  //   JSON.stringify(selectedcatIds || [])
                  // );
                  // formData.append(
                  //   "commercial_register",
                  //   values.commercial_register || ""
                  // );
                  formData.append(
                    "employees_num",
                    String(values.employees_num || 0)
                  );
                  formData.append(
                    "expected_employees_num",
                    String(values.expected_employees_num || 0)
                  );
                  formData.append("company_type", values.company_type || "");
                  formData.append("contacted", values.contacted ? "1" : "0");
                  formData.append("client", values.client || "");
                  formData.append("satisfied", values.satisfied ? "1" : "0");
                  formData.append("culture", values.culture || "");
                  formData.append(
                    "financial_ability",
                    values.financial_ability || ""
                  );
                  formData.append(
                    "service_required",
                    values.service_required || ""
                  );
                  formData.append("our_contact", values.our_contact || "");
                  formData.append(
                    "country_id",
                    String(selectedCountry || values.country)
                  );
                  formData.append(
                    "city_id",
                    String(selectedCity || values.city)
                  );
                  formData.append("email", values.email || "");
                  formData.append(
                    "employee[phone]",
                    values.employee_phone_number || ""
                  );
                  formData.append("employee[position]", values.position || "");
                  formData.append(
                    "employee[employee_email]",
                    values.employee_email || ""
                  );
                  formData.append("employee[mobile]", values.mobile || "");
                  formData.append("employee[title_id]", values.title_id || "");
                  formData.append(
                    "employee[full_name]",
                    values.full_name || ""
                  );
                  formData.append("hr_dept", values.hr_dept ? "1" : "0");
                  formData.append("trusted", values.trusted ? "1" : "0");
                  formData.append(
                    "apply_on_other_site",
                    values.apply_on_other_site ? "1" : "0"
                  );
                  formData.append("apply_url", values.apply_url || "");
                  formData.append("pdf_mode", values.pdf_mode ? "1" : "0");
                  formData.append(
                    "optional_end_date",
                    values.optional_end_date ? "1" : "0"
                  );
                  formData.append(
                    "registration_date",
                    values.registration_date || ""
                  );

                  // Handle company logo file
                  if (
                    values?.company_logo &&
                    typeof values.company_logo !== "string"
                  ) {
                    formData.append("logo", values.company_logo);
                  }

                  const countryCode = selectedMobileCode || "+963";
                  const phoneNumber = values.phone_number || "";
                  const phoneNumberWithCode = `${countryCode}-${phoneNumber}`;
                  formData.append("phone_number", phoneNumberWithCode);

                  selectedActivities.forEach((activity, index) => {
                    formData.append(
                      `company_activity_ids[${index}]`,
                      String(activity.id)
                    );
                  });

                  if (companyId) {
                    await USERAXIOS.post(
                      `/company_profiles/${companyId}`,
                      formData
                    );
                  } else {
                    await USERAXIOS.post("/company_profiles", formData);
                  }

                  openSnackbar({
                    open: true,
                    message: `Company profile ${
                      companyId ? "updated" : "created"
                    } successfully`,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "alert",
                    alert: {
                      color: "success",
                    },
                  } as SnackbarProps);

                  navigate("/companies-list");
                } catch (error) {
                  console.error("Error saving company profile:", error);
                  openSnackbar({
                    open: true,
                    message: `Error ${
                      companyId ? "updating" : "creating"
                    } company profile`,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "alert",
                    alert: {
                      color: "error",
                    },
                  } as SnackbarProps);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                errors,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
                touched,
                values,
              }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{ marginTop: "0px", marginBottom: "2rem" }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              Select User
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="user-select"
                                  style={{ marginBottom: "10px" }}
                                >
                                  User
                                </InputLabel>
                                <FormControl fullWidth>
                                  <InputLabel id="user-select-label">
                                    Select user
                                  </InputLabel>
                                  <Select
                                    labelId="user-select-label"
                                    id="user-select"
                                    value={values.user_id || ""}
                                    label="Select user"
                                    onChange={(e) => {
                                      setFieldValue(
                                        "user_id",
                                        e.target.value || null
                                      );
                                    }}
                                    renderValue={(selected) => {
                                      const user = users.find(
                                        (u) => u.id === selected
                                      );
                                      return user
                                        ? user.user_name ||
                                            user.email ||
                                            String(user.id)
                                        : "";
                                    }}
                                    disabled={!!companyId}
                                  >
                                    {users.map((option) => (
                                      <MenuItem
                                        key={option.id}
                                        value={option.id}
                                      >
                                        {option.user_name ||
                                          option.email ||
                                          String(option.id)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{
                            marginTop: "0px",
                            marginBottom: "2rem",
                          }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              Company Name & Description
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-name_en"
                                  style={{
                                    marginTop: "30px",
                                    marginBottom: "10px",
                                  }}
                                >
                                  Name English
                                </InputLabel>
                                <Field name="name_en">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-name_en"
                                      placeholder="Full Name English"
                                      error={isSubmitting && !!errors.name_en}
                                      helperText={
                                        isSubmitting && errors.name_en
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-name_trans"
                                  style={{
                                    marginBottom: "10px",
                                    marginTop: "30px",
                                  }}
                                >
                                  Name Arabic
                                </InputLabel>
                                <Field name="name_trans.ar">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-name_trans"
                                      placeholder="Full Name Arabic"
                                      error={
                                        isSubmitting && !!errors.name_trans?.ar
                                      }
                                      helperText={
                                        isSubmitting && errors.name_trans?.ar
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-description_en"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Company Description In English
                                </InputLabel>
                                <Field name="description_en">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      id="personal-description_en"
                                      placeholder="Company Description In English"
                                      error={
                                        isSubmitting && !!errors.description_en
                                      }
                                      helperText={
                                        isSubmitting && errors.description_en
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-description_trans"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Company Description In Arabic
                                </InputLabel>
                                <Field name="description_trans.ar">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      id="personal-description_en"
                                      placeholder="Company Description In Arabic"
                                      error={
                                        isSubmitting &&
                                        !!errors.description_trans?.ar
                                      }
                                      helperText={
                                        isSubmitting &&
                                        errors.description_trans?.ar
                                      }
                                    />
                                    // <TextArea
                                    //   color="primary"
                                    //   {...field}
                                    //   fullWidth
                                    //   multiline
                                    //   rows={4}
                                    //   id="personal-description_trans"
                                    //   placeholder="Description Arabic"
                                    //   style={{ width: "100%" }}
                                    //   error={
                                    //     touched.description_trans &&
                                    //     !!errors.description_trans
                                    //   }
                                    //   helperText={
                                    //     touched.description_trans?.ar &&
                                    //     errors.description_trans?.ar
                                    //   }
                                    // />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{
                            marginTop: "0px",
                            marginBottom: "2rem",
                          }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              Contact Information
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <Typography>
                                  <FormattedMessage id="E-mail" />{" "}
                                </Typography>
                                <Field name="email">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-email"
                                      placeholder="Email"
                                      error={isSubmitting && !!errors.email}
                                      helperText={isSubmitting && errors.email}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <Typography>
                                  <FormattedMessage id="Phone" />{" "}
                                </Typography>
                                <Box sx={{ display: "flex" }}>
                                  <PhoneInput
                                    country={"sy"}
                                    value={selectedMobileCode}
                                    onChange={(phone) => {
                                      setSelectedMobileCode(
                                        phone.startsWith("+")
                                          ? phone
                                          : `+${phone}`
                                      );
                                    }}
                                    inputStyle={{
                                      width: "90px",
                                      height: "56px",
                                      fontSize: "16px",
                                      borderRadius: "4px",
                                    }}
                                    containerStyle={{
                                      width: "41px",
                                      position: "relative",
                                      direction: "ltr",
                                    }}
                                    buttonStyle={{
                                      borderRadius: "4px 0 0 4px",
                                    }}
                                    dropdownStyle={{
                                      width: "300px",
                                      maxHeight: "300px",
                                      overflow: "auto",
                                      zIndex: 10000,
                                    }}
                                    searchPlaceholder="Search country..."
                                    searchNotFound="Country not found"
                                    enableSearch={true}
                                    disableSearchIcon={true}
                                    searchStyle={{
                                      width: "90%",
                                      margin: "0 auto",
                                      marginBottom: "10px",
                                    }}
                                    inputProps={{
                                      style: { display: "none" },
                                    }}
                                    countryCodeEditable={false}
                                  />
                                  <Field name="phone_number">
                                    {({ field }: { field: any }) => (
                                      <TextField
                                        {...field}
                                        fullWidth
                                        type="tel"
                                        placeholder="Phone Number"
                                        error={
                                          isSubmitting && !!errors.phone_number
                                        }
                                        helperText={
                                          isSubmitting && errors.phone_number
                                        }
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={1}>
                                <Typography>
                                  <FormattedMessage id="Country" />{" "}
                                </Typography>
                                <Autocomplete
                                  id="country-select"
                                  options={countries || []}
                                  getOptionLabel={(option: Country) =>
                                    theme.direction === "rtl"
                                      ? option.name_trans.ar
                                      : option.name
                                  }
                                  value={
                                    countries?.find(
                                      (country) => country.id === values.country
                                    ) || null
                                  }
                                  onChange={(_, newValue) => {
                                    const selectedCountryId =
                                      newValue?.id || null;
                                    setSelectedCountry(selectedCountryId);
                                    if (selectedCountryId) {
                                      fetchCitiesByCountry(selectedCountryId);
                                    } else {
                                      setCities([]);
                                    }
                                    setFieldValue("country", selectedCountryId);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select country"
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          "& fieldset": {
                                            borderColor: "rgba(0, 0, 0, 0.23)",
                                          },
                                          "&:hover fieldset": {
                                            borderColor: "primary.main",
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: "primary.main",
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                />
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={1}>
                                <Typography>
                                  <FormattedMessage id="City" />{" "}
                                </Typography>
                                <Autocomplete
                                  id="city-select"
                                  options={cities}
                                  getOptionLabel={(option: City) =>
                                    theme.direction === "rtl"
                                      ? option.name_trans.ar
                                      : option.name
                                  }
                                  value={
                                    cities.find(
                                      (city) => city.id === values.city
                                    ) || null
                                  }
                                  onChange={(_, newValue) => {
                                    setSelectedCity(newValue?.id || null);
                                    // setSelectedCityName(newValue?.name || "");
                                    setFieldValue("city", newValue?.id || null);
                                  }}
                                  disabled={!values.country}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select city"
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          "& fieldset": {
                                            borderColor: "rgba(0, 0, 0, 0.23)",
                                          },
                                          "&:hover fieldset": {
                                            borderColor: "primary.main",
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: "primary.main",
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                />
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={1}>
                                <Typography>
                                  <FormattedMessage id="Nationality" />{" "}
                                </Typography>
                                <Field name="nationality_id">
                                  {({
                                    field,
                                    form,
                                  }: {
                                    field: any;
                                    form: any;
                                  }) => (
                                    <Autocomplete
                                      id="nationality-autocomplete"
                                      options={countries || []}
                                      getOptionLabel={(option) =>
                                        theme.direction === "rtl"
                                          ? option.nationality_trans.ar
                                          : option.nationality
                                      }
                                      value={field.value || null}
                                      onChange={(_, newValue) => {
                                        setNationality(newValue?.id || 19);
                                        form.setFieldValue(
                                          "nationality_id",
                                          newValue
                                        );
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          placeholder="Select Nationality"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "& fieldset": {
                                                borderColor:
                                                  "rgba(0, 0, 0, 0.23)",
                                              },
                                              "&:hover fieldset": {
                                                borderColor: "primary.main",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: "primary.main",
                                              },
                                            },
                                          }}
                                        />
                                      )}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6} marginTop=".5rem">
                              <Stack>
                                <Typography>
                                  <FormattedMessage id="Post Office" />{" "}
                                </Typography>
                                <Field name="post_office">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-post_office"
                                      placeholder="Post Office"
                                      error={
                                        isSubmitting && !!errors.post_office
                                      }
                                      helperText={
                                        isSubmitting && errors.post_office
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12}>
                              <Stack>
                                <Typography>
                                  <FormattedMessage id="URL" />{" "}
                                </Typography>
                                <Field name="url">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-url"
                                      placeholder="Url"
                                      error={isSubmitting && !!errors.url}
                                      helperText={isSubmitting && errors.url}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-address_en"
                                  style={{ marginBottom: "10px" }}
                                >
                                  Address English
                                </InputLabel>
                                <Field name="address_en">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      id="personal-address_en"
                                      placeholder="Address English"
                                      error={
                                        isSubmitting && !!errors.address_en
                                      }
                                      helperText={
                                        isSubmitting && errors.address_en
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-address_trans"
                                  style={{ marginBottom: "10px" }}
                                >
                                  Address Arabic
                                </InputLabel>
                                <Field name="address_trans.ar">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      id="personal-address_trans"
                                      placeholder="Address Arabic"
                                      error={
                                        isSubmitting &&
                                        !!errors.address_trans?.ar
                                      }
                                      helperText={
                                        isSubmitting && errors.address_trans?.ar
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{
                            marginTop: "0px",
                            marginBottom: "2rem",
                          }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              Contact Person
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-full_name"
                                  style={{
                                    marginBottom: "10px",
                                    marginTop: "30px",
                                  }}
                                >
                                  Full Name
                                </InputLabel>
                                <Field name="full_name">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-full_name"
                                      placeholder="Full Name"
                                      error={isSubmitting && !!errors.full_name}
                                      helperText={
                                        isSubmitting && errors.full_name
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-employee_email"
                                  style={{
                                    marginBottom: "10px",
                                    marginTop: "30px",
                                  }}
                                >
                                  E-mail
                                </InputLabel>
                                <Field name="employee_email">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-employee_email"
                                      placeholder="Employee Email"
                                      error={
                                        isSubmitting && !!errors.employee_email
                                      }
                                      helperText={
                                        isSubmitting && errors.employee_email
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-title"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Title
                                </InputLabel>
                                <Field name="title_id">
                                  {({ field }: { field: any }) => (
                                    <Select {...field} id="title" fullWidth>
                                      {titles.map((title) => (
                                        <MenuItem
                                          key={title.id}
                                          value={title.id}
                                        >
                                          {theme.direction === "rtl"
                                            ? title.name_trans.ar
                                            : title.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-position"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Position
                                </InputLabel>
                                <Field name="position">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-position"
                                      placeholder="Position"
                                      error={isSubmitting && !!errors.position}
                                      helperText={
                                        isSubmitting && errors.position
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-employee_phone_number"
                                  style={{ marginBottom: "10px" }}
                                >
                                  Contact Person Phone
                                </InputLabel>
                                <Box
                                  sx={{
                                    display: "flex",
                                    position: "relative",
                                    zIndex: 10,
                                  }}
                                >
                                  <PhoneInput
                                    country={"sy"}
                                    value={selectedPhoneCode}
                                    onChange={(phone) => {
                                      setSelectedPhoneCode(
                                        phone.startsWith("+")
                                          ? phone
                                          : `+${phone}`
                                      );
                                    }}
                                    inputStyle={{
                                      width: "90px",
                                      height: "56px",
                                      fontSize: "16px",
                                      borderRadius: "4px",
                                    }}
                                    containerStyle={{
                                      width: "41px",
                                      position: "relative", // important
                                      direction: "ltr",
                                    }}
                                    buttonStyle={{
                                      borderRadius: "4px 0 0 4px",
                                    }}
                                    dropdownStyle={{
                                      position: "absolute",
                                      top: "56px",
                                      right:
                                        theme.direction === "ltr" ? 0 : "10px",
                                      left:
                                        theme.direction === "ltr"
                                          ? "10px"
                                          : "-260px",
                                      width: "300px",
                                      maxHeight: "300px",
                                      overflow: "auto",
                                      zIndex: 9999,
                                    }}
                                    searchPlaceholder="Search country..."
                                    searchNotFound="Country not found"
                                    enableSearch={true}
                                    disableSearchIcon={true}
                                    searchStyle={{
                                      width: "90%",
                                      margin: "0 auto",
                                      marginBottom: "10px",
                                    }}
                                    inputProps={{
                                      style: { display: "none" },
                                    }}
                                    countryCodeEditable={false}
                                  />
                                  <Field name="employee_phone_number">
                                    {({ field }: { field: any }) => (
                                      <TextField
                                        {...field}
                                        fullWidth
                                        type="tel"
                                        placeholder="Contact Person Phone Number"
                                        error={
                                          isSubmitting &&
                                          !!errors.employee_phone_number
                                        }
                                        helperText={
                                          isSubmitting &&
                                          errors.employee_phone_number
                                        }
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-mobile"
                                  style={{ marginBottom: "10px" }}
                                >
                                  Mobile Number
                                </InputLabel>

                                <Box sx={{ display: "flex" }}>
                                  <PhoneInput
                                    country={"sy"}
                                    value={selectedMobileCode}
                                    onChange={(phone) => {
                                      setSelectedMobileCode(
                                        phone.startsWith("+")
                                          ? phone
                                          : `+${phone}`
                                      );
                                    }}
                                    inputStyle={{
                                      width: "90px",
                                      height: "56px",
                                      fontSize: "16px",
                                      borderRadius: "4px",
                                    }}
                                    containerStyle={{
                                      width: "41px",
                                      position: "relative",
                                      direction: "ltr",
                                    }}
                                    buttonStyle={{
                                      borderRadius: "4px 0 0 4px",
                                    }}
                                    dropdownStyle={{
                                      position: "absolute",
                                      top: "56px",
                                      right:
                                        theme.direction === "ltr" ? 0 : "200px",
                                      left:
                                        theme.direction === "ltr"
                                          ? "10px"
                                          : "-260px",
                                      width: "300px",
                                      maxHeight: "300px",
                                      overflow: "auto",
                                      zIndex: 9999,
                                    }}
                                    searchPlaceholder="Search country..."
                                    searchNotFound="Country not found"
                                    enableSearch={true}
                                    disableSearchIcon={true}
                                    searchStyle={{
                                      width: "90%",
                                      margin: "0 auto",
                                      marginBottom: "10px",
                                    }}
                                    inputProps={{
                                      style: { display: "none" },
                                    }}
                                    countryCodeEditable={false}
                                  />
                                  <Field name="mobile">
                                    {({ field }: { field: any }) => (
                                      <TextField
                                        {...field}
                                        fullWidth
                                        type="tel"
                                        placeholder="Mobile Number"
                                        error={isSubmitting && !!errors.mobile}
                                        helperText={
                                          isSubmitting && errors.mobile
                                        }
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{
                            marginTop: "0px",
                            marginBottom: "2rem",
                          }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              Additional Info
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-our_contact"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Our Contact
                                </InputLabel>
                                <Field name="our_contact">
                                  {({ field }: { field: any }) => (
                                    <FormControl fullWidth>
                                      <Select {...field} id="our_contact">
                                        {ourContactOptions.map((option) => (
                                          <MenuItem
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {theme.direction === "rtl"
                                              ? option.arabic_label
                                              : option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-client"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Client
                                </InputLabel>
                                <Field name="client">
                                  {({ field }: { field: any }) => (
                                    <FormControl fullWidth>
                                      <Select {...field} id="client">
                                        {clientOptions.map((option) => (
                                          <MenuItem
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {theme.direction === "rtl"
                                              ? option.arabic_label
                                              : option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-culture"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Culture
                                </InputLabel>
                                <Field name="culture">
                                  {({ field }: { field: any }) => (
                                    <FormControl fullWidth>
                                      <Select {...field} id="culture">
                                        {cultureOptions.map((option) => (
                                          <MenuItem
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {theme.direction === "rtl"
                                              ? option.arabic_label
                                              : option.label}{" "}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-service_required"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Service required
                                </InputLabel>
                                <Field name="service_required">
                                  {({ field }: { field: any }) => (
                                    <FormControl fullWidth>
                                      <Select {...field} id="service_required">
                                        {serviceOptions.map((option) => (
                                          <MenuItem
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {theme.direction === "rtl"
                                              ? option.arabic_label
                                              : option.label}{" "}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-company_type"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Company Type
                                </InputLabel>
                                <Field name="company_type">
                                  {({ field }: { field: any }) => (
                                    <FormControl fullWidth>
                                      <Select {...field} id="company_type">
                                        <MenuItem value={"A"}>A</MenuItem>
                                        <MenuItem value={"B"}>B</MenuItem>
                                        <MenuItem value={"C"}>C</MenuItem>
                                        <MenuItem value={"D"}>D</MenuItem>
                                      </Select>
                                    </FormControl>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-financial_ability"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Financial Ability
                                </InputLabel>
                                <Field name="financial_ability">
                                  {({ field }: { field: any }) => (
                                    <FormControl fullWidth>
                                      <Select {...field} id="financial_ability">
                                        {financialAbilityOptions.map(
                                          (option) => (
                                            <MenuItem
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {theme.direction === "rtl"
                                                ? option.arabic_label
                                                : option.label}
                                            </MenuItem>
                                          )
                                        )}
                                      </Select>
                                    </FormControl>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-employees_num"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Number of Employees
                                </InputLabel>
                                <Field name="employees_num">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      type="number"
                                      id="personal-employees_num"
                                      placeholder="Enter number of employees"
                                      InputProps={{
                                        inputProps: { min: 0 },
                                      }}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-expected_employees_num"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Expected Number of Employees
                                </InputLabel>
                                <Field name="expected_employees_num">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      type="number"
                                      id="personal-expected_employees_num"
                                      placeholder="Enter expected number of employees"
                                      InputProps={{
                                        inputProps: { min: 0 },
                                      }}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-apply_url"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Apply Url
                                </InputLabel>
                                <Field name="apply_url">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-apply_url"
                                      placeholder="Url"
                                      error={isSubmitting && !!errors.apply_url}
                                      helperText={
                                        isSubmitting && errors.apply_url
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-registration_date"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Registration Date
                                </InputLabel>
                                <Field name="registration_date">
                                  {({
                                    field,
                                    form,
                                  }: {
                                    field: any;
                                    form: any;
                                  }) => (
                                    <DatePicker
                                      style={{ width: "100%" }}
                                      value={
                                        field.value ? dayjs(field.value) : null
                                      }
                                      onChange={(date) => {
                                        form.setFieldValue(
                                          field.name,
                                          date
                                            ? date.format("YYYY-MM-DD")
                                            : null
                                        );
                                      }}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-activities"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Company Activities
                                </InputLabel>
                                <Field name="activities">
                                  {({
                                    field,
                                    form,
                                  }: {
                                    field: any;
                                    form: any;
                                  }) => (
                                    <Autocomplete
                                      multiple
                                      id="activities"
                                      options={activities}
                                      getOptionLabel={(option) =>
                                        theme.direction === "rtl"
                                          ? option.name_trans.ar
                                          : option.name
                                      }
                                      value={selectedActivities}
                                      onChange={(_, newValue) => {
                                        setSelectedActivities(newValue);
                                        form.setFieldValue(
                                          "activities",
                                          newValue
                                        );
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          placeholder="Select company activities (max 3)"
                                          helperText="Maximum 3 activities allowed"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "& fieldset": {
                                                borderColor:
                                                  "rgba(0, 0, 0, 0.23)",
                                              },
                                              "&:hover fieldset": {
                                                borderColor: "primary.main",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: "primary.main",
                                              },
                                            },
                                          }}
                                        />
                                      )}
                                      renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                          <Chip
                                            label={
                                              theme.direction === "rtl"
                                                ? option.name_trans.ar
                                                : option.name
                                            }
                                            {...getTagProps({ index })}
                                            sx={{
                                              backgroundColor: "primary.light",
                                              color: "white",
                                              "& .MuiChip-deleteIcon": {
                                                color: "white",
                                                "&:hover": {
                                                  color: "error.main",
                                                },
                                              },
                                            }}
                                          />
                                        ))
                                      }
                                      disabled={false}
                                      disableCloseOnSelect
                                      isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                      }
                                      getOptionDisabled={(option) =>
                                        selectedActivities.length >= 3 &&
                                        !selectedActivities.some(
                                          (activity) =>
                                            activity.id === option.id
                                        )
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Stack>
                                <Typography>
                                  <FormattedMessage id="Logo" />
                                </Typography>
                                <ImageUploader
                                  id="company-logo"
                                  previewUrl={
                                    typeof values.company_logo === "string"
                                      ? values.company_logo
                                      : values.company_logo
                                      ? URL.createObjectURL(values.company_logo)
                                      : null
                                  }
                                  onChange={(e, setFieldValue) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      setFieldValue("company_logo", file);
                                    }
                                  }}
                                  setFieldValue={(field, value) => {
                                    setFieldValue(field, value);
                                  }}
                                  label="Click to upload logo"
                                />
                              </Stack>
                            </Grid>

                            <Grid item xs={0} md={6}></Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-contacted"
                                  style={{
                                    marginBottom: "10px",
                                    marginTop: "30px",
                                  }}
                                >
                                  Contacted
                                </InputLabel>
                                <Box>
                                  <Field name="contacted">
                                    {({ field }: { field: any }) => (
                                      <FormControlLabel
                                        control={
                                          <Box>
                                            <Switch
                                              checked={field.value}
                                              onChange={(e) => {
                                                field.onChange({
                                                  target: {
                                                    name: field.name,
                                                    value: e.target.checked,
                                                  },
                                                });
                                              }}
                                              name="contacted"
                                            />
                                          </Box>
                                        }
                                        label={field.value ? "Yes" : "No"}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-satisfied"
                                  style={{
                                    marginBottom: "10px",
                                    marginTop: "30px",
                                  }}
                                >
                                  Satisfied
                                </InputLabel>
                                <Box>
                                  <Field name="satisfied">
                                    {({ field }: { field: any }) => (
                                      <FormControlLabel
                                        control={
                                          <Box>
                                            <Switch
                                              checked={field.value}
                                              onChange={(e) => {
                                                field.onChange({
                                                  target: {
                                                    name: field.name,
                                                    value: e.target.checked,
                                                  },
                                                });
                                              }}
                                              name="satisfied"
                                            />
                                          </Box>
                                        }
                                        label={field.value ? "Yes" : "No"}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-hr_dept"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  HR Department
                                </InputLabel>
                                <Box>
                                  <Field name="hr_dept">
                                    {({ field }: { field: any }) => (
                                      <FormControlLabel
                                        control={
                                          <Box>
                                            <Switch
                                              checked={field.value}
                                              onChange={(e) => {
                                                field.onChange({
                                                  target: {
                                                    name: field.name,
                                                    value: e.target.checked,
                                                  },
                                                });
                                              }}
                                              name="hr_dept"
                                            />
                                          </Box>
                                        }
                                        label={field.value ? "Yes" : "No"}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-trusted"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Trusted
                                </InputLabel>
                                <Box>
                                  <Field name="trusted">
                                    {({ field }: { field: any }) => (
                                      <FormControlLabel
                                        control={
                                          <Box>
                                            <Switch
                                              checked={field.value}
                                              onChange={(e) => {
                                                field.onChange({
                                                  target: {
                                                    name: field.name,
                                                    value: e.target.checked,
                                                  },
                                                });
                                              }}
                                              name="trusted"
                                            />
                                          </Box>
                                        }
                                        label={field.value ? "Yes" : "No"}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-apply_on_other_site"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Apply on Other Site
                                </InputLabel>
                                <Box>
                                  <Field name="apply_on_other_site">
                                    {({ field }: { field: any }) => (
                                      <FormControlLabel
                                        control={
                                          <Box>
                                            <Switch
                                              checked={field.value}
                                              onChange={(e) => {
                                                field.onChange({
                                                  target: {
                                                    name: field.name,
                                                    value: e.target.checked,
                                                  },
                                                });
                                              }}
                                              name="apply_on_other_site"
                                            />
                                          </Box>
                                        }
                                        label={field.value ? "Yes" : "No"}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-pdf_mode"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Pdf Mode
                                </InputLabel>
                                <Box>
                                  <Field name="pdf_mode">
                                    {({ field }: { field: any }) => (
                                      <FormControlLabel
                                        control={
                                          <Box>
                                            <Switch
                                              checked={field.value}
                                              onChange={(e) => {
                                                field.onChange({
                                                  target: {
                                                    name: field.name,
                                                    value: e.target.checked,
                                                  },
                                                });
                                              }}
                                              name="pdf_mode"
                                            />
                                          </Box>
                                        }
                                        label={field.value ? "Yes" : "No"}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-optional_end_date"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Optional End Date
                                </InputLabel>
                                <Box>
                                  <Field name="optional_end_date">
                                    {({ field }: { field: any }) => (
                                      <FormControlLabel
                                        control={
                                          <Box>
                                            <Switch
                                              checked={field.value}
                                              onChange={(e) => {
                                                field.onChange({
                                                  target: {
                                                    name: field.name,
                                                    value: e.target.checked,
                                                  },
                                                });
                                              }}
                                              name="optional_end_date"
                                            />
                                          </Box>
                                        }
                                        label={field.value ? "Yes" : "No"}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>
                    </Grid>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    marginTop="2rem"
                  >
                    <Grid container justifyContent="center" alignItems="center">
                      <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                        <Grid item md={2} />
                        <Grid item md={4}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => navigate("/training-courses")}
                          >
                            <FormattedMessage id="Cancel" />
                          </Button>
                        </Grid>
                        <Grid item md={4}>
                          <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            <FormattedMessage
                              id={companyId ? "Update" : "Create"}
                            />
                          </Button>
                        </Grid>
                        <Grid item md={2} />
                      </Stack>
                    </Grid>
                  </Stack>
                </form>
              )}
            </Formik>
          </Stack>
        </Stack>
      </section>
    </>
  );
};

export default AddEditCompanyProfile;
